import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import { parseApiErrorMessage, handleApiError } from '../utils/errorUtils';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // load saved auth from local storage on init
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (err) {
        console.error('Error parsing stored user data:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  /**
   * Register a new user 
   * @param {Object|string} usernameOrUserData - Either a username string (old format) or a userData object
   * @param {string} [email] - Email (only used in old format)
   * @param {string} [password] - Password (only used in old format)
   * @param {string} [name] - Optional name (only used in old format)
   * @returns {Promise<Object>} The created user object
   */
  const register = async (usernameOrUserData, email, password, name) => {
    try {
      setLoading(true);
      setError(null);
      
      let userData;
      
      // Check if using the old format (separate parameters) or new format (object)
      if (typeof usernameOrUserData === 'string') {
        // Old format: register(username, email, password, name)
        userData = {
          username: usernameOrUserData,
          email,
          password,
          name,
          turnstile_token: null // Require a valid token
        };
      } else {
        // New format: register(userData)
        userData = usernameOrUserData;
      }
      
      console.log('Registering user:', { 
        username: userData.username, 
        email: userData.email, 
        name: userData.name 
      });
      
      const response = await api.post('/auth/register', userData);
      
      console.log('Registration response:', response.data);
      
      const { access_token, user } = response.data.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setCurrentUser(user);
      return user;
    } catch (err) {
      console.error('Registration error:', err);
      
      // Use standardized error handling
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
      
      // Track specific errors for analytics if needed
      if (errorInfo.message.includes('Username') && (errorInfo.message.includes('taken') || errorInfo.message.includes('registered'))) {
        console.log('Username already exists error');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, preAuthData = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // If we already have auth data from the Turnstile login, use it directly
      if (preAuthData) {
        const { access_token, user } = preAuthData;
        
        // store token and user data
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // set auth header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        setCurrentUser(user);
        return user;
      }
      
      // Otherwise do the normal login flow
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      const response = await api.post('/auth/login', formData);
      
      const { access_token, user } = response.data.data;
      
      // store token and user data
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // set auth header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setCurrentUser(user);
      return user;
    } catch (err) {
      console.error('Login error:', err);
      
      // Use standardized error handling
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithTurnstile = async (username, password, turnstileToken) => {
    try {
      setLoading(true);
      setError(null);
      
      // TESTING MODE: If using the dummy token, bypass Turnstile and use regular login
      if (turnstileToken === 'DUMMY_TOKEN_FOR_TESTING') {
        console.log('TESTING MODE: Bypassing Turnstile verification');
        
        // Use the normal login flow
        const formData = new FormData();
        formData.append('username', username);
        formData.append('password', password);
        const response = await api.post('/auth/login', formData);
        
        const { access_token, user } = response.data.data;
        
        // store token and user data
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // set auth header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        setCurrentUser(user);
        return user;
      }
      
      // Use the proper Turnstile login endpoint
      const response = await api.post('/auth/login-with-turnstile', {
        username,
        password,
        turnstile_token: turnstileToken
      });
      
      const { access_token, user } = response.data.data;
      
      // store token and user data
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // set auth header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setCurrentUser(user);
      return user;
    } catch (err) {
      console.error('Login with Turnstile error:', err);
      
      // Use standardized error handling
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
  };

  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put('/auth/me', userData);
      
      const updatedUser = response.data.data;
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setCurrentUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Profile update error:', err);
      
      // Use standardized error handling
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      currentUser,
      loading,
      error,
      register,
      login,
      loginWithTurnstile,
      logout,
      updateProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};